import { Project } from '@griply/assets';
import { GoalType } from '@griply/types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  DotsMenu12,
  IconFormIcons,
  Lock12,
  Target,
} from 'shared/assets/icons';
import {
  Button,
  Colors,
  Sizes as ButtonSizes,
  Variants,
} from 'shared/components/ui/button';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors as IconButtonColors,
  IconButton,
  Sizes,
} from 'shared/components/ui/icon-button';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import * as Styled from './goal-insights-card.style';

export type HeaderProps = {
  goal: Goal;
  goals: Goal[];
  lifeAreas: LifeArea[];
  menuButtonRef?: React.RefObject<HTMLButtonElement>;
  onMenuButton?: (e: React.MouseEvent) => void;
  onEditGoalProgress?: (e: React.MouseEvent) => void;
  hasBigTitle?: boolean;
};

export const Header: React.FC<HeaderProps> = ({
  goal,
  goals,
  lifeAreas,
  menuButtonRef,
  onMenuButton,
  onEditGoalProgress,
  hasBigTitle,
}) => {
  const { t } = useTranslation();
  const isProject = goal.type === GoalType.Project;

  const color = useMemo(
    () => getLinkedItemColor(goal.id, { goals, lifeAreas }),
    [goal.id, goals, lifeAreas],
  );

  const _onMenuButton = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onMenuButton?.(e);
  };

  const _onUpdate = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onEditGoalProgress?.(e);
  };

  return (
    <Styled.Header>
      <Styled.GoalInformationContainer>
        <Styled.GoalInformationMain>
          <Styled.IllustrationContainer>
            <Styled.ImageContainer
              $iconColor={isProject ? color ?? undefined : undefined}
              $transparent={isProject}
            >
              {isProject ? (
                <Icon icon={Project} />
              ) : goal.image ? (
                <Styled.Image
                  src={goal.image.url}
                  alt={goal.name}
                  hash={goal.image.blurHash}
                />
              ) : (
                <Icon icon={IconFormIcons[goal.iconName!] ?? Target} />
              )}
            </Styled.ImageContainer>

            {!!goal.completedAt && (
              <Styled.CompletedContainer>
                <Icon icon={Check} />
              </Styled.CompletedContainer>
            )}

            {!!color && !isProject && <Styled.ColorDot $color={color} />}
          </Styled.IllustrationContainer>

          <Styled.Title $isFrozen={!!goal.frozenAt} $isCaption={!!hasBigTitle}>
            {goal.name}
          </Styled.Title>
        </Styled.GoalInformationMain>

        <Styled.IconButtonContainer>
          <IconButton
            icon={goal.frozenAt ? Lock12 : DotsMenu12}
            onClick={goal.frozenAt ? undefined : _onMenuButton}
            size={Sizes.ExtraSmall}
            color={IconButtonColors.Default}
            ref={menuButtonRef}
          />
        </Styled.IconButtonContainer>
      </Styled.GoalInformationContainer>

      <Styled.Interactions>
        {!!onEditGoalProgress && (
          <Button
            onClick={_onUpdate}
            variant={Variants.ContainedLight}
            size={ButtonSizes.Small}
            color={Colors.TextPrimary}
          >
            {t('general.labels.insights-card.update-target.short.label')}
          </Button>
        )}
      </Styled.Interactions>
    </Styled.Header>
  );
};
